/**
 * StatusPill (P26).
 *
 * Lifted verbatim from `sample-wireframe/client/src/pages/Home.tsx`. The
 * `.status-pill` and `.status-{tone}` classes live in
 * `apps/web/src/styles/index.css` (lifted same-day). Every status surface
 * across the console uses this — never raw color text.
 */
import type { JSX } from 'react';

export type StatusTone = 'good' | 'warn' | 'danger' | 'info' | 'neutral';

export function StatusPill({
  label,
  tone = 'neutral',
}: {
  label: string;
  tone?: StatusTone;
}): JSX.Element {
  return (
    <span className={`status-pill status-${tone}`}>
      <span aria-hidden />
      {label}
    </span>
  );
}

/**
 * Maps free-form status text to the wireframe's tone palette. Lifted from
 * Home.tsx so column-cell auto-coloring in `<DataTable>` matches.
 */
export function statusToneFrom(value: string | number): StatusTone {
  const text = String(value).toLowerCase();
  if (
    ['allowed', 'healthy', 'verified', 'active', 'approved', 'closed', 'clean'].some((t) =>
      text.includes(t),
    )
  ) {
    return 'good';
  }
  if (
    ['denied', 'disabled', 'blocked', 'open', 'critical', 'failed'].some((t) => text.includes(t))
  ) {
    return 'danger';
  }
  if (
    ['review', 'degraded', 'soon', 'pilot', 'half-open', 'pending', 'rotate'].some((t) =>
      text.includes(t),
    )
  ) {
    return 'warn';
  }
  if (['production', 'guarded', 'info'].some((t) => text.includes(t))) return 'info';
  return 'neutral';
}
